import { css, cx } from '@repo/pandacss/styled-system/css';
import { LinkType } from '@repo/segment/enums';
import { type AnalyticsType } from '@repo/segment/types';
import { EntityPlacementCtaTypeEnum } from '@repo/supergraph/marketplace/enums';
import type { CtaType } from '@repo/supergraph/marketplace/types';
import Image from 'next/image';
import React from 'react';
import { CtaButton } from '../../CtaButton';
import { CtaLink } from '../../CtaLink';

type BankInstitutionCellProps = {
  image?: {
    source?: string;
    alt?: string;
  };
  cta: CtaType | null;
  displayName?: string;
  analyticsPayload: AnalyticsType;
  shouldApplyStylingUpdates?: boolean;
};

const containerBaseStyles = css({
  width: { base: '[175px]', md: '[unset]' },
});

const containerCenteredStyles = css({
  marginInlineStart: 'auto',
  marginInlineEnd: 'auto',
});

const ctaLinkBaseStyles = css({
  display: 'flex',
  '& img': {
    maxWidth: { base: '[122px]', md: '[171px]' },
    maxHeight: { base: '11', md: '[60px]' },
  },
});

const ctaLinkCenteredStyles = css({
  justifyContent: 'center',
});

export const BankInstitutionCell = ({
  image,
  cta,
  displayName,
  analyticsPayload,
  shouldApplyStylingUpdates = false,
}: BankInstitutionCellProps): React.JSX.Element => {
  const BankingCellContent = () => (
    <>
      {image && image.source && (
        <Image
          src={image.source}
          alt={image.alt ?? displayName ?? 'Bank Logo'}
          width={171}
          height={60}
          style={{ objectFit: 'contain' }}
        />
      )}
    </>
  );

  return (
    <div className={cx(containerBaseStyles, shouldApplyStylingUpdates && containerCenteredStyles)}>
      <div>
        {cta ? (
          <CtaLink
            cta={{ ...cta, type: EntityPlacementCtaTypeEnum.ProductImage }}
            monetizable={analyticsPayload.monetizable}
            analyticsPayload={analyticsPayload}
            linkType={LinkType.PRODUCT_IMAGE}
            className={cx(ctaLinkBaseStyles, shouldApplyStylingUpdates && ctaLinkCenteredStyles)}
          >
            <BankingCellContent />
          </CtaLink>
        ) : (
          <BankingCellContent />
        )}
      </div>
      <p
        className={css({
          textStyle: 'textSmall',
          marginTop: '1',
          marginRight: '0',
          marginBottom: '0',
          marginLeft: '0',
        })}
      >
        {displayName}
      </p>
      <div
        className={css({
          display: { base: 'block', md: 'none' },
          marginTop: '2',
          maxWidth: { base: '[189px]', md: '[none]' },
        })}
      >
        {cta && (
          <CtaButton
            cta={cta}
            analyticsPayload={analyticsPayload}
            buttonClassName={css({
              height: { base: '[29px]', md: '[45px]' },
              paddingBlockStart: { base: '2', md: '4' },
              paddingBlockEnd: { base: '2', md: '4' },
              paddingInlineStart: { base: '4', md: '2' },
              paddingInlineEnd: { base: '4', md: '2' },
              width: 'full',
            })}
            className={css({
              columnGap: '0',
              rowGap: '0',
            })}
            subtextContainerClassName={css({
              lineHeight: 'md',
            })}
            subtextClassName={css({
              textAlign: 'center',
              marginTop: '0',
              marginRight: '0',
              marginBottom: '0',
              marginLeft: '0',
            })}
          />
        )}
      </div>
    </div>
  );
};
